import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightLeft, Loader2, UserRound } from "lucide-react";
import { toast } from "react-toastify";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// O outro lado do pedido de transferência: quem está com o atendimento.
//
// Fica montado no layout inteiro porque o pedido chega a qualquer momento e em
// qualquer tela — esperar a pessoa abrir a conversa certa para descobrir que
// um colega precisa dela seria o mesmo que não avisar.
//
// Os pendentes também são buscados ao abrir o sistema: quem estava fora quando
// o pedido saiu encontra o aviso esperando, em vez de nunca saber que existiu.
const SolicitacoesDeTransferencia = () => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const [pedidos, setPedidos] = useState([]);
	const [respondendo, setRespondendo] = useState(false);

	const atual = pedidos[0] || null;

	// Falhar aqui não pode atrapalhar o resto do sistema: tela sem pedido é o
	// estado normal.
	const carregar = useCallback(
		(ativo = { current: true }) =>
			api
				.get("/transfer-requests")
				.then(({ data }) => {
					if (ativo.current) setPedidos(data?.recebidas || []);
				})
				.catch(() => undefined),
		[],
	);

	useEffect(() => {
		if (!user?.id) return undefined;

		const ativo = { current: true };
		carregar(ativo);

		return () => {
			ativo.current = false;
		};
	}, [user?.id, carregar]);

	useEffect(() => {
		if (!user?.id) return undefined;

		const socket = openSocket();

		socket.on("transferRequest", (dados) => {
			if (dados?.action === "create" && dados.pedido) {
				setPedidos((anteriores) =>
					anteriores.some((p) => p.id === dados.pedido.id)
						? anteriores
						: [...anteriores, dados.pedido],
				);
				return;
			}

			// Resposta ao MEU pedido: o desfecho precisa chegar sem eu ter de
			// ficar recarregando a lista para descobrir.
			if (dados?.action === "accepted") {
				toast.success(
					`${dados.pedido?.holder?.name || "O atendente"} transferiu ${
						dados.pedido?.ticket?.contact?.name || "o contato"
					} para você.`,
				);
				return;
			}

			if (dados?.action === "ignored") {
				toast.info(
					`${dados.pedido?.holder?.name || "O atendente"} não transferiu ${
						dados.pedido?.ticket?.contact?.name || "o contato"
					} agora.`,
				);
			}
		});

		return () => socket.disconnect();
	}, [user?.id]);

	const responder = async (aceitar) => {
		if (!atual) return;

		setRespondendo(true);

		try {
			await api.post(
				`/transfer-requests/${atual.id}/${aceitar ? "accept" : "ignore"}`,
			);

			setPedidos((anteriores) => anteriores.filter((p) => p.id !== atual.id));

			if (aceitar) {
				toast.success(
					`Atendimento transferido para ${atual.requester?.name || "o colega"}.`,
				);
			}
		} catch (err) {
			toastError(err);
			// Pedido já respondido por outra aba, ou ticket que mudou de mão:
			// recarregar deixa a tela com o que de fato está pendente.
			carregar();
		}

		setRespondendo(false);
	};

	// Fechar no X não responde nada: o pedido continua pendente no banco e
	// volta a aparecer no próximo carregamento. É "decido depois", não "não".
	const adiar = () =>
		setPedidos((anteriores) => anteriores.filter((p) => p.id !== atual.id));

	if (!atual) return null;

	const contato = atual.ticket?.contact;

	return (
		<Dialog open onOpenChange={(estado) => !estado && adiar()}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<ArrowRightLeft className="h-4 w-4 text-amber-500" />
						Solicitação de transferência
					</DialogTitle>
				</DialogHeader>

				<div className="flex flex-col items-center gap-1 text-center">
					<span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
						<UserRound className="h-6 w-6 text-muted-foreground" />
					</span>
					<p className="font-medium">{contato?.name || "Contato"}</p>
					{contato?.number && (
						<p className="text-xs text-muted-foreground">{contato.number}</p>
					)}
					<p className="mt-2 text-sm">
						<span className="font-medium text-amber-600 dark:text-amber-400">
							{atual.requester?.name || "Um atendente"}
						</span>{" "}
						está solicitando este contato.
					</p>
					<p className="text-xs text-muted-foreground">
						Ticket #{atual.ticketId}
						{atual.ticket?.queue?.name ? ` · ${atual.ticket.queue.name}` : ""}
					</p>
				</div>

				<p className="text-center text-sm text-muted-foreground">
					Deseja transferir o ticket?
				</p>

				<div className="flex justify-center gap-2">
					<Button
						variant="outline"
						onClick={() => responder(false)}
						disabled={respondendo}
					>
						Ignorar
					</Button>
					<Button
						className="gap-2"
						onClick={() => responder(true)}
						disabled={respondendo}
					>
						{respondendo ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<ArrowRightLeft className="h-4 w-4" />
						)}
						Transferir
					</Button>
				</div>

				<Button
					variant="ghost"
					size="sm"
					className="mx-auto text-xs text-muted-foreground"
					onClick={() => navigate(`/tickets/${atual.ticketId}`)}
				>
					Ver a conversa antes de decidir
				</Button>
			</DialogContent>
		</Dialog>
	);
};

export default SolicitacoesDeTransferencia;
